import React, { useEffect, useState } from "react";
import { KeyRound, Loader2, Copy, Check, RefreshCw, ShieldAlert } from "lucide-react";
import api from "../../api";
import { useToast } from "../../hooks/use-toast";

// The secret admin login portal. The admin sets a custom URL, blocks admins from the public login
// page, and can force a fresh 2FA code on every portal sign-in. English by design (admin-only).
export const AdminPortalCard = () => {
  const { toast } = useToast();
  const [state, setState] = useState({ blockCommonLogin: false, forceFreshTotp: false, loginAlerts: true, slug: "" });
  const [slugInput, setSlugInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [meta, setMeta] = useState({});

  const load = async () => {
    const { data } = await api.get("/admin/portal");
    setState({ blockCommonLogin: !!data.blockCommonLogin, forceFreshTotp: !!data.forceFreshTotp, loginAlerts: data.loginAlerts !== false, slug: data.slug || "" });
    setSlugInput(data.slug || "");
    setMeta({ changedAt: data.changedAt, changedBy: data.changedBy });
  };

  useEffect(() => { load().catch(() => {}); }, []);

  const portalUrl = state.slug ? `${window.location.origin}/staff-login/${state.slug}` : "";

  const save = async (override = {}) => {
    const next = { ...state, slug: slugInput.trim(), ...override };
    setBusy(true);
    try {
      const { data } = await api.post("/admin/portal", {
        blockCommonLogin: !!next.blockCommonLogin,
        forceFreshTotp: !!next.forceFreshTotp,
        loginAlerts: !!next.loginAlerts,
        slug: next.slug || null,
      });
      setState({ blockCommonLogin: !!data.blockCommonLogin, forceFreshTotp: !!data.forceFreshTotp, loginAlerts: data.loginAlerts !== false, slug: data.slug || "" });
      setSlugInput(data.slug || "");
      toast({ title: "Portal settings saved" });
      await load();
    } catch (e) {
      toast({ title: "Could not save", description: e.response?.data?.detail || "Try again." });
    } finally { setBusy(false); }
  };

  const regenerate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/portal/regenerate");
      setState((s) => ({ ...s, slug: data.slug || "" }));
      setSlugInput(data.slug || "");
      toast({ title: "New secret URL generated", description: "The old link no longer works." });
      await load();
    } catch (e) {
      toast({ title: "Could not regenerate", description: e.response?.data?.detail || "Try again." });
    } finally { setBusy(false); }
  };

  const copy = async () => {
    if (!portalUrl) return;
    try { await navigator.clipboard.writeText(portalUrl); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch { /* noop */ }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-5 space-y-4" data-testid="admin-portal-card">
      <div>
        <h3 className="font-bold text-foreground flex items-center gap-2">
          <KeyRound size={18} className="text-[#16a34a]" /> Admin login portal
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Move admin sign-in to a secret URL and lock it out of the public login page — useful during
          maintenance so only you can get in.
        </p>
      </div>

      <div className="space-y-2">
        <label className="text-xs font-semibold text-muted-foreground">Secret login path</label>
        <div className="flex items-center gap-2 rounded-xl border border-border bg-background px-3 py-2">
          <span className="text-sm text-muted-foreground shrink-0">/staff-login/</span>
          <input value={slugInput} onChange={(e) => setSlugInput(e.target.value)} data-testid="portal-slug-input"
            placeholder="e.g. s-3f9x2ab7" className="flex-1 bg-transparent outline-none text-sm font-mono text-foreground" />
          <button type="button" onClick={regenerate} disabled={busy} aria-label="Regenerate" data-testid="portal-regenerate"
            className="-m-1 p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors shrink-0"><RefreshCw size={16} /></button>
        </div>
        {portalUrl && (
          <div className="flex items-center gap-2 rounded-lg bg-secondary/50 px-3 py-2">
            <code className="flex-1 text-xs font-mono text-foreground break-all" data-testid="portal-full-url">{portalUrl}</code>
            <button type="button" onClick={copy} aria-label="Copy" data-testid="portal-copy-url"
              className="-m-1 p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors shrink-0">
              {copied ? <Check size={16} className="text-[#16a34a]" /> : <Copy size={16} />}
            </button>
          </div>
        )}
        <p className="text-[11px] text-muted-foreground">8–64 characters: letters, numbers, dashes or underscores. Bookmark it — it won't be shown anywhere else.</p>
      </div>

      <label className="flex items-start gap-3 cursor-pointer rounded-xl border border-border p-3">
        <input type="checkbox" checked={state.blockCommonLogin} data-testid="portal-block-toggle"
          onChange={(e) => setState({ ...state, blockCommonLogin: e.target.checked })}
          className="mt-0.5 h-4 w-4 accent-[#16a34a] shrink-0" />
        <span>
          <span className="block text-sm font-bold text-foreground">Block admin login from the common /login page</span>
          <span className="block text-xs text-muted-foreground mt-0.5">When on, admins can only sign in through the secret URL above. Shoppers and shops are unaffected.</span>
        </span>
      </label>

      <label className="flex items-start gap-3 cursor-pointer rounded-xl border border-border p-3">
        <input type="checkbox" checked={state.forceFreshTotp} data-testid="portal-totp-toggle"
          onChange={(e) => setState({ ...state, forceFreshTotp: e.target.checked })}
          className="mt-0.5 h-4 w-4 accent-[#16a34a] shrink-0" />
        <span>
          <span className="block text-sm font-bold text-foreground">Require a fresh 2FA code every time on the portal</span>
          <span className="block text-xs text-muted-foreground mt-0.5">Ignores the "trust this device" shortcut for portal sign-ins. Only works if the admin account has 2FA (TOTP) enabled.</span>
        </span>
      </label>

      <label className="flex items-start gap-3 cursor-pointer rounded-xl border border-border p-3">
        <input type="checkbox" checked={state.loginAlerts} data-testid="portal-alerts-toggle"
          onChange={(e) => setState({ ...state, loginAlerts: e.target.checked })}
          className="mt-0.5 h-4 w-4 accent-[#16a34a] shrink-0" />
        <span>
          <span className="block text-sm font-bold text-foreground">Email me on every portal sign-in</span>
          <span className="block text-xs text-muted-foreground mt-0.5">Sends an alert with the time, IP address and rough location whenever someone signs in through the secret URL. Goes to the platform alert email.</span>
        </span>
      </label>

      {state.blockCommonLogin && !slugInput.trim() && (
        <p className="flex items-center gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs font-bold text-amber-700 dark:text-amber-300" data-testid="portal-warn-no-slug">
          <ShieldAlert size={14} /> Set a secret path first, or you'll lock yourself out.
        </p>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <button onClick={() => save()} disabled={busy} data-testid="portal-save"
          className="inline-flex items-center gap-2 rounded-xl bg-[#16a34a] hover:bg-[#15803d] text-white text-sm font-bold px-4 py-2 transition-colors disabled:opacity-60">
          {busy ? <Loader2 size={15} className="animate-spin" /> : null} Save settings
        </button>
        {meta.changedAt && (
          <span className="text-xs text-muted-foreground">Last changed {String(meta.changedAt).slice(0, 16).replace("T", " ")} by {meta.changedBy || "admin"}</span>
        )}
      </div>
    </div>
  );
};

export default AdminPortalCard;
